/**
 * DriverOverviewPage — `/driver-portal` (index).
 *
 * Today's overview: live shift status, KPI tiles, the next stops on the
 * active route (live · `GET /v1/routes?driverId=…`), advisories, the pay
 * period summary and quick links into the rest of the portal.
 *
 * Backend status:
 *   - Active route        → live, service-delivery
 *   - Live shift status   → live, GET /v1/driver-portal/me/active-shift
 *   - Advisories          → live, GET /v1/driver-portal/me/advisories
 *   - Distance today      → live, derived from active-shift.distanceMetersLast24h
 *   - Pay period          → live, GET /v1/driver-portal/me/earnings
 */
import { type JSX } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Bell,
  Calendar,
  CheckCircle2,
  Clock,
  CloudRain,
  Gauge,
  Info,
  MapPin,
  Radio,
  ShieldAlert,
  Smartphone,
  Star,
  Truck,
  Wallet,
  X,
  type LucideIcon,
} from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  type DriverAdvisory,
  dismissDriverAdvisory,
  getDriverPortalActiveShift,
  getDriverPortalEarnings,
  listMyActiveRoute,
  listMyDriverAdvisories,
} from '../../lib/api/driver-portal';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';
import { useDriverPortalContext } from './DriverPortalLayout';

function severityClass(severity: string): { bg: string; text: string; border: string } {
  switch (severity) {
    case 'CRITICAL':
      return { bg: 'bg-red-50', text: 'text-red-800', border: 'border-red-200' };
    case 'WARNING':
      return { bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200' };
    default:
      return { bg: 'bg-blue-50', text: 'text-blue-800', border: 'border-blue-200' };
  }
}

function kindIcon(kind: string): LucideIcon {
  switch (kind) {
    case 'WEATHER':
      return CloudRain;
    case 'ROUTE':
      return MapPin;
    case 'KPI':
      return Activity;
    default:
      return Info;
  }
}

export function DriverOverviewPage(): JSX.Element {
  const { driverId, displayName } = useDriverPortalContext();

  const route = useQuery({
    queryKey: ['driver-portal', 'active-route', driverId],
    queryFn: () => listMyActiveRoute({ driverId }),
    retry: false,
    staleTime: 30_000,
  });

  const activeShift = useQuery({
    queryKey: ['driver-portal', 'active-shift', driverId],
    queryFn: () => getDriverPortalActiveShift(driverId),
    retry: false,
    staleTime: 30_000,
  });

  // Earnings — pay-period summary tile (live).
  const earnings = useQuery({
    queryKey: ['driver-portal', 'earnings', driverId],
    queryFn: () => getDriverPortalEarnings(driverId),
    retry: false,
    staleTime: 60_000,
  });

  // Operational advisories — live via service-delivery.
  const queryClient = useQueryClient();
  const advisories = useQuery({
    queryKey: ['driver-portal', 'advisories', driverId],
    queryFn: () => listMyDriverAdvisories(driverId),
    retry: false,
    staleTime: 30_000,
  });
  const dismissMutation = useMutation({
    mutationFn: (advisoryId: string) => dismissDriverAdvisory(advisoryId, driverId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['driver-portal', 'advisories', driverId],
      });
    },
  });
  const shift = activeShift.data?.shift ?? null;
  const lastGps = activeShift.data?.lastGpsAt ? new Date(activeShift.data.lastGpsAt) : null;
  const distanceKm = activeShift.data ? (activeShift.data.distanceMetersLast24h / 1000).toFixed(1) : '—';
  const distanceTodayLabel = activeShift.data ? `${distanceKm} km` : '—';

  function formatNaira(minor: string | null | undefined, currency = 'NGN'): string {
    if (minor == null) return '—';
    try {
      const big = BigInt(minor);
      const major = big / 100n;
      const remainder = big % 100n;
      const padded = remainder.toString().padStart(2, '0');
      const symbol = currency === 'NGN' ? '₦' : `${currency} `;
      return `${symbol}${major.toLocaleString()}.${padded}`;
    } catch {
      return '—';
    }
  }
  const earningsData = earnings.data ?? null;

  const denied =
    route.isError &&
    route.error instanceof ApiError &&
    (route.error.status === 401 || route.error.status === 403);
  const active = route.data ?? null;
  const stops = active?.stops ?? [];
  const completed = stops.filter((s) => s.status === 'COMPLETED').length;
  const remaining = stops.filter(
    (s) => s.status === 'PENDING' || s.status === 'ARRIVED',
  ).length;

  return (
    <div>
      <PageHeader
        title={`Today, ${displayName.split(/\s+/)[0] ?? ''}`.trim()}
        description="Live shift overview. Field actions happen on the mobile app."
        actions={
          <Link
            to="/driver-portal/schedule"
            className="inline-flex items-center gap-2 bg-nipost-green text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-nipost-green-dark transition-colors"
          >
            View schedule <ArrowRight className="w-4 h-4" />
          </Link>
        }
      />

      {/* Live shift status — wired to GET /v1/driver-portal/me/active-shift. */}
      <div className="bg-white border border-border rounded-xl p-5 mb-6" data-testid="live-shift">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Radio className="w-4 h-4 text-nipost-green" /> Live shift status
            </h3>
            <p className="text-xs text-muted">
              {shift
                ? `Shift ${shift.id.slice(0, 8)}… started ${new Date(shift.startedAt).toLocaleTimeString()}`
                : 'No active shift — clock in via the mobile app to start telemetry.'}
            </p>
          </div>
          <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${
            shift ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
          }`}>
            {shift ? 'Live' : 'Off shift'}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Stops attempted', value: shift ? String(shift.stopsAttempted) : '—', icon: Activity },
            { label: 'Stops delivered', value: shift ? String(shift.stopsDelivered) : '—', icon: Radio },
            { label: 'Distance 24h',    value: `${distanceKm} km`, icon: Gauge },
            { label: 'Last GPS',        value: lastGps ? lastGps.toLocaleTimeString() : '—', icon: Activity },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="bg-gray-50 rounded-lg p-3">
                <p className="text-[10px] text-muted uppercase tracking-widest font-semibold flex items-center gap-1">
                  <Icon className="w-3 h-3" /> {s.label}
                </p>
                <p className="text-lg font-bold text-gray-900 mt-1">{s.value}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* KPI tiles — derived from active route where possible, otherwise — */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard
          title="Stops planned"
          value={active ? String(active.stopCount) : '—'}
          hint="Stops planned on the active route"
          icon={Calendar}
        />
        <KPICard
          title="Completed"
          value={active ? String(completed) : '—'}
          hint="route.stops[].status == COMPLETED"
          icon={CheckCircle2}
          tone="success"
        />
        <KPICard
          title="Remaining"
          value={active ? String(remaining) : '—'}
          hint="PENDING + ARRIVED"
          icon={Clock}
        />
        <KPICard
          title="Distance today"
          value={distanceTodayLabel}
          hint="active-shift.distanceMetersLast24h"
          icon={MapPin}
          tone={activeShift.data ? 'default' : 'warning'}
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Next stops */}
        <div className="lg:col-span-2 bg-white border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">
              Next stops (engine-sequenced)
            </h3>
            <Link
              to="/driver-portal/schedule"
              className="text-xs text-nipost-green hover:underline"
            >
              See full schedule
            </Link>
          </div>
          {route.isPending ? (
            <p className="text-sm text-muted">Loading active route…</p>
          ) : denied ? (
            <DeferredNotice title="Permission required">
              The dev token doesn&apos;t carry{' '}
              <code className="font-mono">Delivery/readRoute</code>.
            </DeferredNotice>
          ) : route.isError ? (
            <DeferredNotice title="Service unreachable">
              {(route.error as Error).message}
            </DeferredNotice>
          ) : !active ? (
            <p className="text-sm text-muted">
              No active route assigned to you right now. Check back when your
              dispatcher assigns one.
            </p>
          ) : (
            <div className="space-y-3">
              {stops
                .filter((s) => s.status !== 'COMPLETED')
                .slice(0, 6)
                .map((s) => (
                  <div
                    key={s.id}
                    className="flex items-start gap-3 p-3 rounded-lg bg-gray-50"
                  >
                    <div className="w-9 h-9 rounded-full bg-white border-2 border-nipost-green flex items-center justify-center text-xs font-bold text-nipost-green flex-shrink-0">
                      {s.sequence}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {s.contactName ?? 'Recipient'}
                      </p>
                      <p className="text-xs text-muted truncate">
                        {typeof s.address === 'string'
                          ? s.address
                          : (s.address as { line1?: string })?.line1 ?? '—'}
                      </p>
                      {s.mailItemId ? (
                        <p className="text-[10px] text-muted mt-0.5 font-mono">
                          {s.mailItemId}
                        </p>
                      ) : null}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-[10px] text-muted">{s.kind}</p>
                      <p className="text-[10px] text-muted">{s.status}</p>
                    </div>
                  </div>
                ))}
              {stops.filter((s) => s.status !== 'COMPLETED').length === 0 ? (
                <p className="text-sm text-muted text-center py-4">
                  All stops on this route are complete.
                </p>
              ) : null}
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* Advisories — live via service-delivery driver-advisories module. */}
          <div
            className="bg-white border border-border rounded-xl p-5"
            data-testid="driver-advisories"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-warning" /> Advisories
              </h3>
              <Link
                to="/driver-portal/notifications"
                className="text-xs text-nipost-green hover:underline"
              >
                All
              </Link>
            </div>
            {advisories.isPending ? (
              <p className="text-xs text-muted">Loading advisories…</p>
            ) : advisories.isError ? (
              <DeferredNotice title="Advisories feed unreachable">
                {(advisories.error as Error).message}
              </DeferredNotice>
            ) : (advisories.data?.items.length ?? 0) === 0 ? (
              <p className="text-xs text-muted py-3 text-center">
                No active advisories. You're clear to drive.
              </p>
            ) : (
              <ul className="space-y-2">
                {advisories.data?.items.slice(0, 4).map((a: DriverAdvisory) => {
                  const palette = severityClass(a.severity);
                  const Icon = kindIcon(a.kind);
                  return (
                    <li
                      key={a.id}
                      className={`rounded-lg border p-3 ${palette.bg} ${palette.border} flex items-start gap-3`}
                    >
                      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${palette.text}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold ${palette.text}`}>{a.title}</p>
                        <p className="text-xs text-gray-700 mt-0.5">{a.body}</p>
                        <p className="text-[10px] text-muted mt-1 uppercase tracking-wider">
                          {a.kind} · {a.severity}
                        </p>
                      </div>
                      <button
                        type="button"
                        aria-label={`Dismiss advisory ${a.title}`}
                        disabled={dismissMutation.isPending}
                        onClick={() => dismissMutation.mutate(a.id)}
                        className="text-muted hover:text-gray-700 disabled:opacity-50"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Earnings tile — live (GET /v1/driver-portal/me/earnings). */}
          <div
            className="bg-white border border-border rounded-xl p-5"
            data-testid="overview-pay-period"
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Wallet className="w-4 h-4 text-nipost-gold" /> Pay period
              </h3>
              <Link
                to="/driver-portal/earnings"
                className="text-xs text-nipost-green hover:underline"
              >
                Details
              </Link>
            </div>
            <p className="text-2xl font-extrabold text-gray-900" data-testid="overview-pay-net">
              {formatNaira(earningsData?.netMinorUnits, earningsData?.currency)}
            </p>
            <p className="text-xs text-muted">
              {earnings.isError
                ? 'Earnings feed unreachable.'
                : earningsData
                  ? 'Net so far this period.'
                  : 'Loading…'}
            </p>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
              <div className="bg-gray-50 rounded p-2">
                <p className="text-muted">Stops</p>
                <p className="font-semibold text-gray-900">
                  {earningsData ? String(earningsData.stopsDelivered) : '—'}
                </p>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <p className="text-muted">On-time</p>
                <p className="font-semibold text-gray-900">
                  {earningsData?.onTimePercent != null
                    ? `${earningsData.onTimePercent}%`
                    : '—'}
                </p>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <p className="text-muted">Hours</p>
                <p className="font-semibold text-gray-900 inline-flex items-center gap-0.5">
                  {earningsData ? `${earningsData.hoursWorked}h` : '—'}{' '}
                  <Star className="w-3 h-3 text-nipost-gold" />
                </p>
              </div>
            </div>
          </div>

          {/* Quick links */}
          <div className="bg-white border border-border rounded-xl p-3">
            <p className="text-[10px] uppercase tracking-widest text-muted font-semibold px-2 py-1.5">
              Quick links
            </p>
            {[
              { to: '/driver-portal/scorecard',     label: 'My scorecard',      icon: Activity },
              { to: '/driver-portal/safety',        label: 'Safety & cases',    icon: ShieldAlert },
              { to: '/driver-portal/notifications', label: 'Notifications',     icon: Bell },
              { to: '/driver-portal/profile',       label: 'Profile & banking', icon: Truck },
              { to: '/',                            label: 'Operator console',  icon: Smartphone, accent: true },
            ].map((q) => {
              const Icon = q.icon;
              return (
                <Link
                  key={q.to}
                  to={q.to}
                  className={`flex items-center justify-between gap-2 px-2 py-2 rounded-lg hover:bg-gray-50 text-sm ${
                    q.accent ? 'text-nipost-green font-semibold' : 'text-gray-700'
                  }`}
                >
                  <span className="inline-flex items-center gap-2">
                    <Icon
                      className={`w-4 h-4 ${
                        q.accent ? 'text-nipost-green' : 'text-gray-400'
                      }`}
                    />{' '}
                    {q.label}
                  </span>
                  <ArrowRight
                    className={`w-4 h-4 ${
                      q.accent ? 'text-nipost-green' : 'text-gray-300'
                    }`}
                  />
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
